import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Database,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import {
  useIntegritySummary,
  useCreateExclusion,
  useRemoveExclusion,
  useUpdateFindingStatus,
  useActionLogs,
} from '../hooks/useDataIntegrity.js';
import type { EnrichedFinding, FindingStatus, Severity } from '../types/data-integrity.js';
import {
  SEVERITY_ORDER,
  SeveritySummaryBar,
  StatusBadge,
  STATUS_LABEL_KEYS,
} from '../components/data-integrity/IntegrityBadges.js';
import { HealthScoreGauge, StatusCountBar } from '../components/data-integrity/HealthScoreGauge.js';
import { ActionQueueCard } from '../components/data-integrity/ActionQueueCard.js';
import { FindingRow } from '../components/data-integrity/FindingRow.js';

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business CQRS PG/Mongo データ整合性オペレーションダッシュボード。健全性スコア・要対応キュー・ステータス管理で「予期せぬデータ差異に気づきUXを毀損する前に処置を施す」運用フローを実現する。
 * @graph-connects useIntegritySummary [calls] サマリ取得
 * @graph-connects useCreateExclusion [calls] 除外作成
 * @graph-connects useRemoveExclusion [calls] 除外削除
 * @graph-connects useUpdateFindingStatus [calls] ステータス更新
 * @graph-connects useActionLogs [calls] アクションログ取得
 */
export function DataIntegrityPage() {
  const { t } = useTranslation();
  const [filterSeverity, setFilterSeverity] = useState<Severity | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<FindingStatus | 'all'>('all');
  const [showExcluded, setShowExcluded] = useState(false);
  const [excludeTarget, setExcludeTarget] = useState<EnrichedFinding | null>(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [statusTarget, setStatusTarget] = useState<{
    finding: EnrichedFinding;
    newStatus: FindingStatus;
  } | null>(null);
  const [statusNote, setStatusNote] = useState('');
  const [activeTab, setActiveTab] = useState<'dashboard' | 'findings' | 'history'>('dashboard');

  const { data: summary, isLoading, refetch } = useIntegritySummary();
  const createExclusion = useCreateExclusion();
  const removeExclusion = useRemoveExclusion();
  const updateStatus = useUpdateFindingStatus();
  const { data: actionLogData } = useActionLogs();

  const filteredFindings = useMemo(() => {
    if (!summary) return [];
    let results = summary.findings;
    if (!showExcluded) results = results.filter((f) => !f.excluded);
    if (filterSeverity !== 'all')
      results = results.filter((f) => SEVERITY_ORDER[f.severity] <= SEVERITY_ORDER[filterSeverity]);
    if (filterStatus !== 'all')
      results = results.filter((f) => f.operationalStatus === filterStatus);
    return results;
  }, [summary, showExcluded, filterSeverity, filterStatus]);

  const handleExclude = (finding: EnrichedFinding) => {
    setExcludeTarget(finding);
    setExcludeReason('');
  };
  const confirmExclude = () => {
    if (!excludeTarget || !excludeReason.trim()) return;
    createExclusion.mutate(
      {
        entity: excludeTarget.entity,
        recordId: excludeTarget.recordId,
        reason: excludeReason.trim(),
      },
      {
        onSuccess: () => {
          setExcludeTarget(null);
        },
      },
    );
  };
  const handleRestore = (finding: EnrichedFinding) => {
    removeExclusion.mutate(finding.id);
  };
  const handleStatusChange = (finding: EnrichedFinding, newStatus: FindingStatus) => {
    setStatusTarget({ finding, newStatus });
    setStatusNote('');
  };
  const confirmStatusChange = () => {
    if (!statusTarget || !statusNote.trim()) return;
    updateStatus.mutate(
      {
        findingId: statusTarget.finding.id,
        input: {
          previousStatus: statusTarget.finding.operationalStatus,
          newStatus: statusTarget.newStatus,
          note: statusNote.trim(),
        },
      },
      {
        onSuccess: () => {
          setStatusTarget(null);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="loading-spinner" />
          <span className="text-sm text-[var(--color-text-muted)]">
            {t('dataIntegrity.checking')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-6 w-6 text-[var(--color-accent-primary)]" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
              {t('dataIntegrity.title')}
            </h1>
            <p className="mt-0.5 text-sm text-[var(--color-text-muted)]">
              {t('dataIntegrity.description')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {summary && (
            <span className="text-xs text-[var(--color-text-muted)]">
              {new Date(summary.checkedAt).toLocaleString('ja-JP')}
            </span>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              void refetch();
            }}
          >
            <RefreshCw className="mr-1 h-4 w-4" aria-hidden="true" />
            {t('dataIntegrity.recheck')}
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[var(--color-border)]">
        {[
          { key: 'dashboard' as const, labelKey: 'dataIntegrity.tabDashboard' },
          { key: 'findings' as const, labelKey: 'dataIntegrity.tabFindings' },
          { key: 'history' as const, labelKey: 'dataIntegrity.tabHistory' },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => {
              setActiveTab(tab.key);
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === tab.key ? 'border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]' : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'}`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && summary && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Card className="p-4">
              <HealthScoreGauge health={summary.healthScore} />
            </Card>
            <Card className="p-4 space-y-3">
              <div className="text-sm font-medium text-[var(--color-text-primary)]">
                {t('dataIntegrity.actionStatus')}
              </div>
              <StatusCountBar counts={summary.statusCounts} />
              <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)]">
                <span className="font-mono font-bold">
                  {t('dataIntegrity.totalDiffCount', { num: String(summary.totalDiff) })}
                </span>
                {summary.exclusionCount > 0 && (
                  <span>
                    {t('dataIntegrity.excludedCount', { num: String(summary.exclusionCount) })}
                  </span>
                )}
              </div>
            </Card>
          </div>
          <Card className="p-4">
            <SeveritySummaryBar counts={summary.severityCounts} />
          </Card>
          <ActionQueueCard findings={summary.actionRequired} onStatusChange={handleStatusChange} />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {summary.entities.map((entity) => (
              <Card key={entity.entity} className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-[var(--color-text-primary)]">
                    {entity.label}
                  </span>
                  {entity.status === 'ok' && (
                    <CheckCircle2
                      className="h-4 w-4 text-[var(--color-success,#22c55e)]"
                      aria-hidden="true"
                    />
                  )}
                  {entity.status === 'warning' && (
                    <AlertTriangle
                      className="h-4 w-4 text-[var(--color-warning,#f59e0b)]"
                      aria-hidden="true"
                    />
                  )}
                  {entity.status === 'error' && (
                    <XCircle className="h-4 w-4 text-[var(--color-error)]" aria-hidden="true" />
                  )}
                </div>
                <div className="grid grid-cols-3 gap-1 text-xs">
                  <div>
                    <div className="text-[var(--color-text-muted)]">PG</div>
                    <div className="font-mono">
                      {entity.pgCount === -1 ? '-' : entity.pgCount.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-[var(--color-text-muted)]">Mongo</div>
                    <div className="font-mono">
                      {entity.mongoCount === -1 ? '-' : entity.mongoCount.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-[var(--color-text-muted)]">{t('dataIntegrity.diff')}</div>
                    <div
                      className={`font-mono ${entity.diff !== 0 ? 'text-[var(--color-error)] font-bold' : ''}`}
                    >
                      {entity.diff > 0 ? '+' : ''}
                      {entity.diff}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <Card className="p-4">
            <h3 className="text-sm font-bold text-[var(--color-text-primary)] mb-3">
              {t('dataIntegrity.operationFlow')}
            </h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              {[
                {
                  step: '1',
                  titleKey: 'dataIntegrity.flowDetectTitle',
                  descKey: 'dataIntegrity.flowDetectDesc',
                },
                {
                  step: '2',
                  titleKey: 'dataIntegrity.flowReviewTitle',
                  descKey: 'dataIntegrity.flowReviewDesc',
                },
                {
                  step: '3',
                  titleKey: 'dataIntegrity.flowActTitle',
                  descKey: 'dataIntegrity.flowActDesc',
                },
                {
                  step: '4',
                  titleKey: 'dataIntegrity.flowCompleteTitle',
                  descKey: 'dataIntegrity.flowCompleteDesc',
                },
              ].map((item) => (
                <div key={item.step} className="flex gap-3 items-start">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-primary)]/10 text-xs font-bold text-[var(--color-accent-primary)]">
                    {item.step}
                  </div>
                  <div>
                    <div className="text-xs font-medium text-[var(--color-text-primary)]">
                      {t(item.titleKey)}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)]">{t(item.descKey)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'findings' && (
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
            <h2 className="text-sm font-bold text-[var(--color-text-primary)]">
              {t('dataIntegrity.allFindingsTitle')}
            </h2>
            <div className="flex items-center gap-3">
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value as FindingStatus | 'all');
                }}
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1 text-xs text-[var(--color-text-primary)]"
              >
                <option value="all">{t('dataIntegrity.allStatuses')}</option>
                <option value="open">{t('dataIntegrity.statusOpen')}</option>
                <option value="acknowledged">{t('dataIntegrity.acknowledged')}</option>
                <option value="in_progress">{t('dataIntegrity.statusInProgress')}</option>
                <option value="resolved">{t('dataIntegrity.resolved')}</option>
                <option value="wont_fix">{t('dataIntegrity.statusWontFix')}</option>
              </select>
              <select
                value={filterSeverity}
                onChange={(e) => {
                  setFilterSeverity(e.target.value as Severity | 'all');
                }}
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1 text-xs text-[var(--color-text-primary)]"
              >
                <option value="all">{t('dataIntegrity.allSeverities')}</option>
                <option value="critical">{t('dataIntegrity.criticalOnly')}</option>
                <option value="high">{t('dataIntegrity.severityHighOrAbove')}</option>
                <option value="medium">{t('dataIntegrity.severityMediumOrAbove')}</option>
                <option value="low">{t('dataIntegrity.severityLowOrAbove')}</option>
              </select>
              <label className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={showExcluded}
                  onChange={(e) => {
                    setShowExcluded(e.target.checked);
                  }}
                  className="rounded border-[var(--color-border)]"
                />
                {t('dataIntegrity.showExcluded')}
              </label>
            </div>
          </div>
          {filteredFindings.length === 0 ? (
            <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
              {t('dataIntegrity.noFindings')}
            </div>
          ) : (
            <div>
              {filteredFindings.map((finding) => (
                <FindingRow
                  key={finding.id}
                  finding={finding}
                  onExclude={handleExclude}
                  onRestore={handleRestore}
                  onStatusChange={handleStatusChange}
                />
              ))}
            </div>
          )}
        </Card>
      )}

      {activeTab === 'history' && (
        <Card className="overflow-hidden p-0">
          <div className="border-b border-[var(--color-border)] px-4 py-3">
            <h2 className="text-sm font-bold text-[var(--color-text-primary)]">
              {t('dataIntegrity.tabHistory')}
            </h2>
          </div>
          {actionLogData && actionLogData.actionLogs.length > 0 ? (
            <div className="divide-y divide-[var(--color-border)]">
              {actionLogData.actionLogs.map((log) => (
                <div key={log.id} className="px-4 py-3 flex items-start gap-3">
                  <Clock
                    className="h-4 w-4 mt-0.5 shrink-0 text-[var(--color-text-muted)]"
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-medium text-[var(--color-text-primary)]">
                        {log.actionBy}
                      </span>
                      <span className="text-[var(--color-text-muted)]">
                        {t(STATUS_LABEL_KEYS[log.previousStatus])} →{' '}
                        {t(STATUS_LABEL_KEYS[log.newStatus])}
                      </span>
                      <span className="font-mono text-[var(--color-text-muted)]">
                        {log.entity}:
                        {log.recordId.length > 8 ? `${log.recordId.slice(0, 8)}...` : log.recordId}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-[var(--color-text-secondary)]">{log.note}</p>
                    <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                      {new Date(log.createdAt).toLocaleString('ja-JP')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
              {t('dataIntegrity.noHistory')}
            </div>
          )}
        </Card>
      )}

      {excludeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-[var(--color-text-primary)]">
              {t('dataIntegrity.excludeFindingTitle')}
            </h3>
            <p className="mt-2 font-mono text-sm text-[var(--color-text-secondary)]">
              {t('dataIntegrity.excludeConfirm', {
                target: `${excludeTarget.entityLabel}:${excludeTarget.recordId}`,
              })}
            </p>
            <div className="mt-4">
              <label
                htmlFor="exclude-reason"
                className="block text-sm font-medium text-[var(--color-text-primary)]"
              >
                {t('dataIntegrity.exclusionReason')}
              </label>
              <input
                id="exclude-reason"
                type="text"
                value={excludeReason}
                onChange={(e) => {
                  setExcludeReason(e.target.value);
                }}
                placeholder={t('dataIntegrity.exclusionReasonPlaceholder')}
                className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)]"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setExcludeTarget(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={confirmExclude}
                disabled={!excludeReason.trim() || createExclusion.isPending}
              >
                {t('dataIntegrity.excludeSubmit')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {statusTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-[var(--color-text-primary)]">
              {t('dataIntegrity.changeStatus')}
            </h3>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
              <span className="font-mono">
                {statusTarget.finding.entityLabel}:{statusTarget.finding.recordId}
              </span>
            </p>
            <div className="mt-2 flex items-center gap-2 text-sm">
              <StatusBadge status={statusTarget.finding.operationalStatus} />
              <ArrowRight className="h-4 w-4 text-[var(--color-text-muted)]" aria-hidden="true" />
              <StatusBadge status={statusTarget.newStatus} />
            </div>
            <div className="mt-4">
              <label
                htmlFor="status-note"
                className="block text-sm font-medium text-[var(--color-text-primary)]"
              >
                {t('dataIntegrity.actionNote')}
              </label>
              <input
                id="status-note"
                type="text"
                value={statusNote}
                onChange={(e) => {
                  setStatusNote(e.target.value);
                }}
                placeholder={t('dataIntegrity.actionNotePlaceholder')}
                className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)]"
              />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setStatusTarget(null);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={confirmStatusChange}
                disabled={!statusNote.trim() || updateStatus.isPending}
              >
                {t('dataIntegrity.changeStatusSubmit')}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
